import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { KeyRound, LogIn, Search, Send, ChevronDown, Ban, RotateCcw } from 'lucide-react';
import { useT } from '../../i18n';
import { authFetch } from '../../lib/auth';
import { PageHead, Card, fmtDateTime } from './os2ui';

/* Identity Center — Identity is the highest level in Kitxens. Everything else belongs to an identity. */

const API = process.env.REACT_APP_BACKEND_URL;

function OrgTabs({ isEs, active }) {
  const tabs = [
    { to: '/admin/ai-os/restaurants', key: 'org', en: 'Organization', es: 'Organización' },
    { to: '/admin/ai-os/users', key: 'identity', en: 'Identity', es: 'Identidad' },
  ];
  return (
    <div className="mb-4 inline-flex rounded-full border border-gray-200 bg-white p-1" data-testid="org-tabs">
      {tabs.map((t) => (
        <a key={t.key} href={t.to}
          className={`rounded-full px-4 py-1.5 text-[12px] font-bold transition-colors ${active === t.key ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}
          data-testid={`org-tab-${t.key}`}>
          {isEs ? t.es : t.en}
        </a>
      ))}
    </div>
  );
}

function StatusPill({ s }) {
  const map = {
    active: ['bg-emerald-50 text-emerald-700 border-emerald-200', '✅ Active'],
    invited: ['bg-amber-50 text-amber-700 border-amber-200', '✉️ Invited'],
    disabled: ['bg-red-50 text-red-600 border-red-200', '❌ Disabled'],
  };
  const [cls, label] = map[s] || ['bg-gray-50 text-gray-500 border-gray-200', s || '—'];
  return <span className={`rounded-full border px-2 py-0.5 text-[10.5px] font-bold whitespace-nowrap ${cls}`}>{label}</span>;
}

function WorkspaceRow({ w, isEs, onAction }) {
  const [msg, setMsg] = useState(null);
  const act = async (fn, label) => {
    setMsg({ busy: true });
    const m = await fn();
    setMsg(m);
    if (m?.reloadable) onAction();
  };
  const reset = () => act(async () => {
    const r = await authFetch(`/admin/restaurants/${w.rid}/users/${w.uid}/reset-password`, { method: 'POST' });
    const j = await r.json();
    return r.ok ? { ok: true, text: `${isEs ? 'Nueva contraseña (se muestra UNA vez)' : 'New password (shown ONCE)'}: ${j.new_password}`, reloadable: true } : { ok: false, text: j.detail || 'error' };
  });
  const loginAs = async () => {
    if (!window.confirm(isEs ? `¿Entrar al OS como ${w.email}? (queda auditado)` : `Open the OS as ${w.email}? (audited)`)) return;
    const r = await authFetch(`/admin/restaurants/${w.rid}/users/${w.uid}/impersonate`, { method: 'POST' });
    const j = await r.json();
    if (r.ok) window.location.href = j.path; else setMsg({ ok: false, text: j.detail || 'error' });
  };
  const toggle = () => act(async () => {
    const action = w.status === 'disabled' ? 'enable' : 'disable';
    const r = await authFetch(`/admin/restaurants/${w.rid}/users/${w.uid}/${action}`, { method: 'POST' });
    const j = await r.json();
    return r.ok ? { ok: true, text: action === 'disable' ? (isEs ? 'Acceso revocado — datos conservados.' : 'Access revoked — data kept.') : (isEs ? 'Acceso restaurado.' : 'Access restored.'), reloadable: true } : { ok: false, text: j.detail || 'error' };
  });
  const sendLink = () => act(async () => {
    const r = await fetch(`${API}/api/auth/recover`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: w.email }) });
    return { ok: r.ok, text: r.ok ? (isEs ? '📬 Enlace de acceso enviado (15 min).' : '📬 Access link emailed (15 min).') : 'error' };
  });
  const btn = 'inline-flex items-center gap-1 rounded-full border border-gray-300 px-2.5 py-1 text-[11px] font-bold text-gray-600 hover:border-gray-600 transition-colors';
  return (
    <div className="rounded-xl border border-gray-100 px-3.5 py-2.5" data-testid={`identity-ws-${w.workspace?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12.5px]">
        <span className="font-semibold text-gray-900">{w.workspace}</span>
        <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] uppercase font-bold text-gray-500">{w.role}</span>
        <StatusPill s={w.status} />
        <span className="text-gray-400 text-[11.5px] tabular-nums">{isEs ? 'Login' : 'Login'}: {w.last_login ? fmtDateTime(w.last_login) : '—'}</span>
        <div className="ml-auto flex items-center gap-1.5">
          {w.kind === 'restaurant' && (
            <>
              <button onClick={reset} className={btn} data-testid={`identity-reset-${w.rid}`}><KeyRound className="w-3 h-3" /> Reset</button>
              {w.status === 'active' && (
                <button onClick={loginAs} data-testid={`identity-loginas-${w.rid}`}
                  className="inline-flex items-center gap-1 rounded-full bg-gray-900 hover:bg-[#F77F11] px-2.5 py-1 text-[11px] font-bold text-white transition-colors">
                  <LogIn className="w-3 h-3" /> {isEs ? 'Entrar como' : 'Login as'}
                </button>
              )}
              <button onClick={toggle} data-testid={`identity-toggle-${w.rid}`}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors ${w.status === 'disabled' ? 'border border-emerald-300 text-emerald-600 hover:bg-emerald-50' : 'border border-red-200 text-red-500 hover:bg-red-50'}`}>
                {w.status === 'disabled' ? <><RotateCcw className="w-3 h-3" /> {isEs ? 'Restaurar' : 'Restore'}</> : <><Ban className="w-3 h-3" /> {isEs ? 'Revocar' : 'Revoke'}</>}
              </button>
            </>
          )}
          {w.kind === 'core' && (
            <button onClick={sendLink} className={btn} data-testid={`identity-resetlink-${w.email}`}><Send className="w-3 h-3" /> {isEs ? 'Reset link' : 'Reset link'}</button>
          )}
        </div>
      </div>
      {msg && !msg.busy && <p className={`mt-1.5 text-[12px] font-semibold ${msg.ok ? 'text-emerald-700' : 'text-red-600'}`} data-testid="identity-action-msg">{msg.text}</p>}
    </div>
  );
}

function IdentityCard({ id, isEs, onAction }) {
  const [open, setOpen] = useState(false);
  const [audit, setAudit] = useState(null);
  useEffect(() => {
    if (open && audit === null) {
      authFetch(`/admin/restaurants/identities/audit?email=${encodeURIComponent(id.email)}`)
        .then((r) => r.json()).then((j) => setAudit(j.events || [])).catch(() => setAudit([]));
    }
  }, [open, audit, id.email]);
  return (
    <Card className="mb-3" data-testid={`identity-card-${id.email}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-start gap-3.5 p-4 text-left" data-testid={`identity-expand-${id.email}`}>
        <span className="w-10 h-10 shrink-0 rounded-full bg-gray-900 text-white flex items-center justify-center text-[13px] font-black">
          {(id.name || id.email).slice(0, 1).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[14.5px] font-bold text-gray-900 leading-tight">{id.name || id.email}</p>
          <p className="text-[12px] text-gray-400">{id.email}</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {id.roles.map((r) => <span key={r} className="rounded-full bg-orange-50 border border-orange-200 text-[#d8651a] px-2 py-0.5 text-[10.5px] font-bold">✓ {r}</span>)}
            {id.companies.map((c) => <span key={c} className="rounded-full bg-gray-100 px-2 py-0.5 text-[10.5px] font-bold text-gray-500">🏢 {c}</span>)}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[11px] font-bold text-gray-500">{id.workspaces.length} {isEs ? (id.workspaces.length === 1 ? 'workspace' : 'workspaces') : (id.workspaces.length === 1 ? 'workspace' : 'workspaces')}</p>
          <p className="text-[11px] text-gray-400 tabular-nums">{isEs ? 'Último acceso' : 'Last access'}: {id.last_login ? fmtDateTime(id.last_login) : (isEs ? 'Nunca' : 'Never')}</p>
          <ChevronDown className={`ml-auto mt-1 w-4 h-4 text-gray-300 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2">
          {id.workspaces.map((w, i) => <WorkspaceRow key={i} w={w} isEs={isEs} onAction={onAction} />)}
          <div className="rounded-xl bg-gray-50 border border-gray-100 p-3.5" data-testid={`identity-audit-${id.email}`}>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-2">Audit Log</p>
            {audit === null && <p className="text-[12px] text-gray-400">…</p>}
            {audit && audit.length === 0 && <p className="text-[12px] text-gray-400">{isEs ? 'Sin eventos registrados para esta identidad.' : 'No recorded events for this identity.'}</p>}
            {(audit || []).slice(0, 10).map((e, i) => (
              <p key={i} className="text-[11.5px] text-gray-500 py-0.5">
                <span className="font-bold text-gray-700">{e.type}</span> · {e.source} · <span className="tabular-nums">{fmtDateTime(e.created_at)}</span>
                {e.payload?.restaurant && <span className="text-gray-400"> · {e.payload.restaurant}</span>}
              </p>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function UsersPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const [params] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState(params.get('q') || '');

  const load = () => authFetch('/admin/restaurants/identities/all').then((r) => r.json()).then((j) => setRows(j.identities || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const filtered = (rows || []).filter((id) =>
    !q.trim() || `${id.email} ${id.name} ${id.companies.join(' ')} ${id.roles.join(' ')} ${id.workspaces.map((w) => w.workspace).join(' ')}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-identity-center">
      <OrgTabs isEs={isEs} active="identity" />
      <PageHead title={isEs ? 'Identidad' : 'Identity'}
        question={isEs ? 'La identidad es el nivel más alto en Kitxens. Roles, workspaces, accesos y auditoría — todo pertenece a una identidad.' : 'Identity is the highest level in Kitxens. Roles, workspaces, access and audit — everything belongs to an identity.'} />
      <div className="relative max-w-sm mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={isEs ? 'Buscar identidad, empresa o rol…' : 'Search identity, company or role…'}
          className="w-full rounded-xl border border-gray-200 pl-9 pr-3 py-2.5 text-[13px]" data-testid="identity-search" />
      </div>
      {rows === null && <p className="text-[13px] text-gray-400">…</p>}
      {filtered.map((id) => <IdentityCard key={id.email} id={id} isEs={isEs} onAction={load} />)}
      {rows !== null && filtered.length === 0 && <p className="text-[13px] text-gray-400">{isEs ? 'Sin resultados.' : 'No results.'}</p>}
      <p className="mt-3 text-[11px] text-gray-400">
        {isEs ? '"Revocar" desactiva el acceso conservando todos los datos. "Entrar como" queda auditado en el bus de eventos.' : '"Revoke" disables access while keeping all data. "Login as" is audited via the event bus.'}
      </p>
    </div>
  );
}
